import type { HelpContent } from './types';

export const settingsHubHelp: HelpContent = {
  pageTitle: 'Settings',
  description: 'The Settings hub is where administrators configure company-wide options, integrations, roles, templates, and notifications.',

  functionality: {
    overview:
      'The Settings hub provides access to all application configuration areas. From here you can navigate to each settings section based on the areas your role has permission to manage.',
    keyFeatures: [
      {
        label: 'General Settings',
        detail: 'Configure company details, reporting calculations, dashboard targets, and audit trail behaviour.',
        requiredPermission: 'settings.generalSettings',
      },
      {
        label: 'Project Settings',
        detail: 'Define project creation rules, status labels, custom fields, budget thresholds, and billing configuration.',
        requiredPermission: 'settings.projectSettings',
      },
      {
        label: 'Integrations',
        detail: 'Connect and manage third-party finance systems and other data sources.',
        requiredPermission: 'settings.integrations',
      },
      {
        label: 'Roles & Permissions',
        detail: 'Create and edit roles, assign users to roles, and control what each role can see and do.',
        requiredPermission: 'settings.rolesAndPermissions',
      },
      {
        label: 'Invoice & Email Templates',
        detail: 'Build and manage invoice layouts and automated email templates used in client communications.',
      },
      {
        label: 'Notifications',
        detail: 'Choose which system events trigger notifications and configure when and how they are sent.',
      },
    ],
    sectionsAndFields: [
      {
        name: 'General Settings card',
        description: 'Opens the General Settings page for company details, reporting options, and audit configuration.',
        requiredPermission: 'settings.generalSettings',
      },
      {
        name: 'Project Settings card',
        description: 'Opens the Project Settings page for project creation, status labels, custom fields, and budgets.',
        requiredPermission: 'settings.projectSettings',
      },
      {
        name: 'Integrations card',
        description: 'Opens the Integrations page where you connect finance systems and other external data sources.',
        requiredPermission: 'settings.integrations',
      },
      {
        name: 'Roles & Permissions card',
        description: 'Opens the Roles & Permissions page to manage user roles and access control.',
        requiredPermission: 'settings.rolesAndPermissions',
      },
      {
        name: 'Invoice & Email Templates card',
        description: 'Opens the templates page to create and manage invoice layouts and email templates.',
      },
      {
        name: 'Notifications card',
        description: 'Opens the Notifications settings page to configure alerts and scheduled reminders.',
      },
    ],
    useCases: [
      'An administrator needs to connect a new finance system integration.',
      'A team lead wants to create a new role with limited project visibility.',
      'An administrator needs to update the company logo before issuing client invoices.',
      'A user wants to configure which events trigger notification emails.',
    ],
  },

  userGuide: {
    gettingStarted:
      'Click any card to open that settings section. Only cards your role has permission to access will be visible.',
    steps: [
      {
        title: 'How do I navigate to a settings section?',
        items: [
          { text: 'Click the settings card for the area you want to configure.' },
          { text: 'The selected settings page opens immediately.' },
          {
            text: 'Use the browser back button or the breadcrumbs at the top of the page to return to this hub.',
            tip: 'You can also press Ctrl/Cmd + , from anywhere in the app to jump directly to Settings.',
          },
        ],
      },
      {
        title: 'How do I know which settings I can access?',
        items: [
          {
            text: 'Only settings cards your role has permission to access are shown on this page.',
            note: 'If you need access to a settings area that is not shown, ask your administrator to update your role permissions.',
          },
        ],
      },
    ],
    proTips: [
      'Press Ctrl/Cmd + , from any page to jump straight to Settings.',
      'Changes made in Settings apply company-wide — review the impact before saving.',
      'Use the User Preferences page (your profile menu) for personal settings that only affect your own account.',
    ],
  },

  faq: [
    {
      question: 'Why can I only see some settings cards?',
      answer:
        'The cards shown depend on your role permissions. If you need access to an area that is not visible, ask an administrator to update your role.',
    },
    {
      question: 'What is the difference between Settings and User Preferences?',
      answer:
        'Settings apply company-wide to all users. User Preferences (found in your profile menu) apply only to your own account — such as your default landing page and sidebar position.',
    },
    {
      question: 'Can I access Settings from a keyboard shortcut?',
      answer: 'Yes. Press Ctrl/Cmd + , from anywhere in the application to open Settings.',
    },
  ],

  relatedPages: [
    { label: 'General Settings', path: '/settings/general' },
    { label: 'Project Settings', path: '/settings/project' },
    { label: 'Roles & Permissions', path: '/settings/user-permissions' },
    { label: 'User Preferences', path: '/settings/user-preferences' },
  ],
};
